import {
  ArrowUpRight, Sparkles, ShieldCheck, Users, HeartHandshake,
  FileText, ClipboardList, Calculator, BookOpen, Lock, Check
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeading } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const SECTIONS = [
  {
    id: "carers",
    eyebrow: "For Carers",
    accent: "purple",
    title: "Resources for independent carers",
    description: "Whether you're just starting out or building a long-term independent practice, choose the access level that fits where you are right now.",
    cards: [
      {
        slug: "free-carer-access",
        tier: "free",
        accent: "purple",
        icon: Sparkles,
        title: "Free Carer Resources",
        audience: "For self-employed or independent carers",
        body: "A welcoming starting point — foundational guidance, sample templates and the essentials every independent carer should understand before working privately.",
        includes: [
          "Starter templates and checklists",
          "Plain-English guidance on private work",
          "Introductory articles and explainers",
        ],
        cta: "Open Free Carer Access",
        href: "/join/carer-starter",
        requiredEntitlement: "carer_free",
        internalHref: "/workspace/carer-starter",
      },
      {
        slug: "pro-carer-circle",
        tier: "paid",
        accent: "purple",
        icon: ShieldCheck,
        title: "Pro Carer Office",
        audience: "For carers building a professional independent practice",
        body: "The full back office: the Shift Toolkit, written agreements, payment protection, tax help, pricing calculators, CPD guidance and the templates that make independent work sustainable.",
        includes: [
          "Shift Toolkit and every template",
          "Tax Toolkit + pricing calculator",
          "Payment protection resources",
          "CPD tracker and training guidance",
        ],
        cta: "Open Pro Carer Office",
        href: "/membership/pro-carer-circle",
        requiredEntitlement: "pro_carer",
        internalHref: "/workspace/pro-carer-circle/start-here",
      },
    ],
  },
  {
    id: "families",
    eyebrow: "For Families",
    accent: "teal",
    title: "Resources for families arranging care",
    description: "Clear, practical support for families thinking about — or already managing — independent care for a loved one.",
    cards: [
      {
        slug: "free-family-access",
        tier: "free",
        accent: "teal",
        icon: Users,
        title: "Free Family Resources",
        audience: "For families exploring independent care",
        body: "Start here. Understand how independent care works, the questions to ask, and the basics of arranging private care safely and fairly.",
        includes: [
          "Introductory guides for families",
          "Sample interview questions",
          "Plain-English overview of independent care",
        ],
        cta: "Open Free Family Resources",
        href: "/join/family-starter",
        requiredEntitlement: "family_free",
        internalHref: "/workspace/family-starter",
      },
      {
        slug: "family-membership",
        tier: "paid",
        accent: "teal",
        icon: HeartHandshake,
        title: "Family Membership Access",
        audience: "For families ready to arrange independent care",
        body: "Everything a family needs in one place: written agreements, payment confirmations, care schedule templates, safety checklists and cost guidance — designed to make private arrangements clearer for everyone involved.",
        includes: [
          "Care Booking Agreement",
          "Responsible Payer Confirmation",
          "Deposit & Payment Agreement",
          "Care Schedule & Expectations Template",
        ],
        cta: "Open Family Membership",
        href: "/membership/family-circle",
        requiredEntitlement: "family_circle",
        internalHref: "/workspace/family-circle",
      },
    ],
  },
];

function TierBadge({ tier, accent }) {
  if (tier === "free") {
    return (
      <span className="pill bg-slate-100 text-ink-muted" data-testid="tier-free-badge">
        <Check className="w-3.5 h-3.5 mr-1" /> Free access
      </span>
    );
  }
  const cls = accent === "purple" ? "bg-purple-50 text-purple" : "bg-teal-50 text-teal";
  return (
    <span className={`pill ${cls}`} data-testid="tier-paid-badge">
      <Lock className="w-3.5 h-3.5 mr-1" /> Members
    </span>
  );
}

function ResourceCard({ card, hasAccess }) {
  const accentText = card.accent === "purple" ? "text-purple" : "text-teal";
  const accentBg = card.accent === "purple" ? "bg-purple-50" : "bg-teal-50";
  const btnClass = card.accent === "purple" ? "btn-purple" : "btn-teal";
  const isInternalNoAccessLink = !hasAccess && card.href.startsWith("/");
  const Wrapper = (hasAccess || isInternalNoAccessLink) ? Link : "a";
  const wrapperProps = hasAccess
    ? { to: card.internalHref }
    : isInternalNoAccessLink
    ? { to: card.href }
    : { href: card.href, target: "_blank", rel: "noopener noreferrer" };
  return (
    <Wrapper
      {...wrapperProps}
      className="card-base p-7 md:p-8 flex flex-col group hover:-translate-y-1 transition-transform duration-300"
      data-testid={`resource-card-${card.slug}`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className={`w-12 h-12 rounded-xl ${accentBg} ${accentText} flex items-center justify-center`}>
          <card.icon className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <TierBadge tier={card.tier} accent={card.accent} />
      </div>
      <h3 className="font-display text-xl md:text-2xl font-semibold leading-snug">{card.title}</h3>
      <p className={`mt-1 text-sm font-medium ${accentText}`}>{card.audience}</p>
      <p className="mt-3 text-ink-muted text-sm leading-relaxed">{card.body}</p>
      <ul className="mt-5 space-y-2 text-sm text-ink-muted">
        {card.includes.map((item) => (
          <li key={item} className="flex gap-2">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${accentText}`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
        <span className={`${btnClass} text-sm py-2.5 px-5`} data-testid={`cta-${card.slug}`}>
          {card.cta}
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </Wrapper>
  );
}

const TYPE_GRID = [
  { icon: FileText, label: "Templates & agreements" },
  { icon: ClipboardList, label: "Checklists & toolkits" },
  { icon: Calculator, label: "Calculators" },
  { icon: BookOpen, label: "Plain-English guides" },
];

export default function ResourceLibrary() {
  const session = useHandoverSession();
  const holds = (entitlement) => session.active && session.entitlements.includes(entitlement);
  // "Start as a carer/family" reassurance CTAs: send an already-signed-in
  // member (of any tier on that side) straight to their own hub instead of
  // an external sign-up page they've already completed.
  const carerHubHref = holds("pro_carer")
    ? "/workspace/pro-carer-circle/start-here"
    : holds("carer_free") ? "/workspace/carer-starter" : null;
  const familyHubHref = holds("family_circle")
    ? "/workspace/family-circle"
    : holds("family_free") ? "/workspace/family-starter" : null;

  return (
    <div data-testid="page-resources">
      <PageHero
        eyebrow="The JCC Resource Library"
        title="Resources That Actually Help"
        description="Helpful templates, practical tools and calculators designed for real-world care — explained in plain English."
      />

      {/* What's inside (informational, not the gated resources themselves) */}
      <section className="py-12 md:py-16 border-b border-slate-100">
        <div className="container-jcc">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {TYPE_GRID.map((t) => (
              <div key={t.label} className="flex items-center gap-3 p-4 rounded-2xl bg-surface" data-testid={`type-${t.label.toLowerCase().split(' ')[0]}`}>
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-ink shrink-0">
                  <t.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <span className="text-sm font-medium text-ink">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience sections */}
      {SECTIONS.map((section, idx) => (
        <section
          key={section.id}
          className={`section ${idx % 2 === 1 ? "bg-surface" : ""}`}
          data-testid={`section-${section.id}`}
        >
          <div className="container-jcc">
            <SectionHeading
              eyebrow={section.eyebrow}
              accent={section.accent}
              title={section.title}
              description={section.description}
            />
            <div className="mt-10 md:mt-12 grid md:grid-cols-2 gap-6 md:gap-8">
              {section.cards.map((card) => (
                <ResourceCard key={card.slug} card={card} hasAccess={holds(card.requiredEntitlement)} />
              ))}
            </div>
            {section.id === "carers" && (
              <Link
                to="/carer-quiz"
                className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-purple/20 bg-purple/5 p-5 hover:bg-purple/10 transition-colors"
                data-testid="resources-carer-quiz-banner"
              >
                <span>
                  <span className="font-display font-semibold text-ink">New: Carer Reflection Quiz</span>
                  <span className="block text-sm text-ink-muted mt-0.5">Free, 10 questions, no sign-up — explore your strengths and habits as a carer.</span>
                </span>
                <ArrowUpRight className="w-5 h-5 text-purple shrink-0" />
              </Link>
            )}
          </div>
        </section>
      ))}

      {/* Reassurance footer */}
      <section className="section">
        <div className="container-jcc">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 text-center max-w-3xl mx-auto" data-testid="resources-reassurance">
            <h3 className="font-display text-2xl md:text-3xl font-semibold leading-snug">Not sure which area is right for you?</h3>
            <p className="mt-3 text-ink-muted leading-relaxed">
              Both free areas are a good starting point. You can always upgrade later when you're ready — there's no pressure, and nothing is hidden behind sales pages.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {carerHubHref ? (
                <Link to={carerHubHref} className="btn-outline" data-testid="reassurance-cta-carer">
                  Start as a carer
                </Link>
              ) : (
                <Link
                  to="/join/carer-starter"
                  className="btn-outline"
                  data-testid="reassurance-cta-carer"
                >
                  Start as a carer
                </Link>
              )}
              {familyHubHref ? (
                <Link to={familyHubHref} className="btn-outline" data-testid="reassurance-cta-family">
                  Start as a family
                </Link>
              ) : (
                <Link
                  to="/join/family-starter"
                  className="btn-outline"
                  data-testid="reassurance-cta-family"
                >
                  Start as a family
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
